import React, { useState, useEffect, useMemo, useRef } from "react";
import UserService from "../services/UserService";
import { useAuth } from "react-oidc-context";
import { TextField, Autocomplete } from "@mui/material";
import { debounce } from "lodash";

interface EmailOption {
  label: string;
  value: string;
}



interface EmailPickerSingleProps {
  selectedEmail: string;
  setSelectedEmail: React.Dispatch<React.SetStateAction<string>>;
  setValidationError: React.Dispatch<React.SetStateAction<string>>;
  className?: string;
}

const EmailPickerSingle: React.FC<EmailPickerSingleProps> = ({
  selectedEmail,
  setSelectedEmail,
  setValidationError,
  className
}) => {
  const [emailOptions, setEmailOptions] = useState<EmailOption[]>([]);
  const [email, setEmail] = useState<EmailOption | null>(
    selectedEmail ? { label: selectedEmail, value: selectedEmail } : null
  );

  useEffect(() => {
    setValidationError("");
    if (selectedEmail) {
      setInputValue(selectedEmail);
      setEmail({ label: selectedEmail, value: selectedEmail });
    }
  }, [selectedEmail,setValidationError]);

  const [inputValue, setInputValue] = useState(selectedEmail || "");

  const auth = useAuth();
  const abortControllerRef = useRef<AbortController | null>(null);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const loadEmailOptions = useMemo(() => {
    const fetchOptions = async (input: string) => {
      // if (!input || input.length < 3) return;

      // Abort previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await UserService.getUsers(
          auth.user?.access_token as string,
          input,
          "userName",
          "asc",
          1,
          100,
          controller
        );
        const options = response?.rows?.map((user: { email: string }) => ({
          label: user.email,
          value: user.email,
        }));


        if (validateEmail(input)) {
          setEmailOptions([{ label: input, value: input }]);
        } else {
          if (options?.length > 0)
            setEmailOptions(options);
          setValidationError("invalid email address!")
        }
      } catch (error: any) {
        if (error.name === "CanceledError") {
          // Request was canceled
          return;
        }
        console.error("Failed to fetch users:", error);
      }
    };

    return debounce(fetchOptions, 300);
  }, [auth.user?.access_token, setValidationError]);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      loadEmailOptions.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadEmailOptions]);

  return (
    <Autocomplete
      className={className}
      freeSolo
      options={emailOptions}
      value={email}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);

        if (newInputValue.trim() !== "") {
          const formattedEmail = { label: newInputValue, value: newInputValue };
          setEmail(formattedEmail);
          setSelectedEmail(newInputValue);
          setValidationError("");
          loadEmailOptions(newInputValue);
        } else {
          setValidationError("Invalid email address.");
        }
      }}
      onChange={(_, selectedOption) => {
        if (typeof selectedOption === "string") {
          const newOption = { label: selectedOption, value: selectedOption };
          setEmail(newOption);
          setSelectedEmail(selectedOption);
          setValidationError("");
        } else if (selectedOption) {
          setEmail(selectedOption);
          setSelectedEmail(selectedOption.value);
          setValidationError("");
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label=""
          error={!!inputValue && inputValue.trim() === ""}
          helperText={inputValue && inputValue.trim() === "" ? "Invalid email address." : ""}
          InputProps={{
            ...params.InputProps,
            style: {
              height: "39px",
              fontSize: "14px",
              padding: "5px",
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "black", borderWidth: "0.1px" }, // Thin dark gray border
              "& input": { fontWeight: "normal" } // Ensures normal font weight
            }
          }}
        />
      )}
    />
  );
};

export default EmailPickerSingle;
